import React, { useState } from 'react';
import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Button,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorOutline } from '@mui/icons-material';
import { Alert } from '@mui/material';
import * as Sentry from '@sentry/react';
import { format } from 'date-fns';
import { BaseCurrencies } from '@joindeed/currency';
import _ from 'lodash';

import {
  useCreateInvoiceForCorporateGivingMutation,
  CreateInvoiceForCorporateGivingMutationVariables,
  DonationDisbursementCorporateGivingType,
  usePendingCorporateGivingDisbursementsQuery,
  TotalByGivingAccountAndMonth,
  useGivingAccountsSummaryQuery,
} from 'generated/graphql';
import { currencyFormat } from 'utils/currencyFormat';
import { selectUser } from 'entities/user/selectors';
import { showErrorAction, showMessageAction } from 'containers/Notification/actions';

import { InvoiceCreationModal } from './InvoiceCreationModal';

const CorporateGivingTable = ({
  header,
  children,
}: {
  header: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box mb={4}>
    <Paper style={{ overflow: 'hidden' }}>
      <TableContainer>
        <Box display="flex" justifyContent="space-between" alignItems="center" m={2}>
          {header}
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Total Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </Paper>
  </Box>
);

const CorporateGivingDisbursementTable = ({
  donationDisbursementType,
  disbursementTotals,
  givingAccountId,
  getRecommendedPaymentDueDate,
  dateTo,
  refetch,
}: {
  donationDisbursementType: DonationDisbursementCorporateGivingType;
  disbursementTotals: TotalByGivingAccountAndMonth[];
  givingAccountId: string;
  getRecommendedPaymentDueDate: () => string;
  dateTo: Date;
  refetch: () => void;
}) => {
  const [createInvoice, { loading: createInvoiceLoading }] =
    useCreateInvoiceForCorporateGivingMutation({
      update: (cache) => {
        cache.evict({ id: 'ROOT_QUERY', fieldName: 'invoices' });
        cache.gc();
      },
      onCompleted: () => {
        // Refetch pending disbursements
        refetch();
      },
    });

  const { data: givingAccountsData } = useGivingAccountsSummaryQuery();
  const givingAccounts = givingAccountsData?.givingAccounts || [];

  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [paymentDueDate, setPaymentDueDate] = useState(getRecommendedPaymentDueDate());

  const handleCreateInvoice = async () => {
    try {
      const variables: CreateInvoiceForCorporateGivingMutationVariables = {
        donationDisbursementType,
        dateTo: dateTo.toISOString(),
        givingAccountId,
      };

      if (poNumber.trim()) {
        variables.poNumber = poNumber.trim();
      }

      if (paymentDueDate) {
        variables.paymentDueDate = new Date(paymentDueDate).toISOString();
      }

      await createInvoice({
        variables,
      });
      setModalOpen(false);
      setPoNumber('');
      dispatch(showMessageAction('Invoice created successfully.'));
    } catch (e) {
      dispatch(showErrorAction('Failed to create an invoice. Please try again.'));
      console.error(e);
      Sentry.captureException(e);
    }
  };

  const handleOpenModal = () => {
    setPoNumber('');
    setPaymentDueDate(getRecommendedPaymentDueDate());
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setPoNumber('');
    setPaymentDueDate(getRecommendedPaymentDueDate());
  };

  const generateInvoiceButton = (
    <Button variant="contained" color="primary" onClick={handleOpenModal}>
      Generate {donationDisbursementType} Invoice
    </Button>
  );

  const givingAccount = givingAccounts.find((g) => g.id === givingAccountId);

  const confirmationModal = (
    <InvoiceCreationModal
      open={modalOpen}
      onClose={handleCloseModal}
      onConfirm={handleCreateInvoice}
      poNumber={poNumber}
      onPoNumberChange={setPoNumber}
      paymentDueDate={paymentDueDate}
      onPaymentDueDateChange={setPaymentDueDate}
      isLoading={createInvoiceLoading}
      donationDisbursementType={donationDisbursementType}
      givingAccountName={givingAccount?.name || givingAccountId}
      dateTo={dateTo}
    />
  );

  return (
    <>
      <CorporateGivingTable
        header={
          <>
            <Typography variant="subtitle1" component="div" style={{ fontWeight: 500 }}>
              {donationDisbursementType} Disbursements - {givingAccount?.name || givingAccountId}
            </Typography>
            {generateInvoiceButton}
          </>
        }
      >
        {createInvoiceLoading ? (
          <TableRow>
            <TableCell colSpan={5} align="center">
              <LinearProgress />
            </TableCell>
          </TableRow>
        ) : (
          disbursementTotals.map((totals) => (
            <TableRow key={`${totals.year}-${totals.month}`}>
              <TableCell>
                {format(
                  new Date(Number(totals.year), Number(totals.month) - 1, 1),
                  'MMM, yyyy'
                ).toUpperCase()}
              </TableCell>
              <TableCell>{currencyFormat(totals.total, totals.currencyCode)}</TableCell>
            </TableRow>
          ))
        )}
        <TableRow>
          <TableCell colSpan={2} align="right">
            Total Pending Deductions:{' '}
            {currencyFormat(
              disbursementTotals.reduce((acc, total) => acc + total.total, 0),
              disbursementTotals[0]?.currencyCode || BaseCurrencies.USD
            )}
          </TableCell>
        </TableRow>
      </CorporateGivingTable>

      {confirmationModal}
    </>
  );
};

export const CreateInvoiceCorporateGiving = ({
  donationDisbursementType,
  dateTo,
  getRecommendedPaymentDueDate,
}: {
  donationDisbursementType: DonationDisbursementCorporateGivingType;
  dateTo: Date;
  getRecommendedPaymentDueDate: () => string;
}) => {
  const user = useSelector(selectUser);
  const { data, loading, error, refetch } = usePendingCorporateGivingDisbursementsQuery({
    variables: {
      donationDisbursementType,
      dateTo: dateTo.toISOString(),
    },
  });

  if (user && !user.organization.disbursementConfiguration?.invoicing[donationDisbursementType]) {
    return null;
  }

  const disbursementTotals = data?.pendingCorporateGivingDisbursements?.items || [];

  if (error) {
    return (
      <Alert icon={<ErrorOutline />} severity="error">
        There was an error loading disbursements data. Please try again.
      </Alert>
    );
  }

  if (loading) {
    return (
      <CorporateGivingTable
        header={
          <Typography variant="subtitle1" component="div" style={{ fontWeight: 500 }}>
            {donationDisbursementType} Disbursements
          </Typography>
        }
      >
        <TableRow>
          <TableCell colSpan={5} align="center">
            <LinearProgress />
          </TableCell>
        </TableRow>
      </CorporateGivingTable>
    );
  }

  if (disbursementTotals.length === 0) {
    return (
      <CorporateGivingTable
        header={
          <Typography variant="subtitle1" component="div" style={{ fontWeight: 500 }}>
            {donationDisbursementType} Disbursements
          </Typography>
        }
      >
        <TableRow>
          <TableCell colSpan={5} align="center">
            No pending disbursements found in the selected time period
          </TableCell>
        </TableRow>
      </CorporateGivingTable>
    );
  }

  const disbursementTotalsByGivingAccount = _.groupBy(disbursementTotals, (d) => d.givingAccountId);

  return (
    <>
      {Object.entries(disbursementTotalsByGivingAccount).map(
        ([givingAccountId, disbursementTotalsForGivingAccount]) => (
          <CorporateGivingDisbursementTable
            key={givingAccountId}
            donationDisbursementType={donationDisbursementType}
            givingAccountId={givingAccountId}
            disbursementTotals={disbursementTotalsForGivingAccount}
            getRecommendedPaymentDueDate={getRecommendedPaymentDueDate}
            dateTo={dateTo}
            refetch={refetch}
          />
        )
      )}
    </>
  );
};
